import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Phone, Menu, X } from "lucide-react";
import { BUSINESS } from "@/data/site";

const LINKS = [
  { label: "Meet Nicole", href: "#meet-nicole" },
  { label: "Services", href: "#services" },
  { label: "Reviews", href: "#reviews" },
  { label: "Contact", href: "#contact" },
];

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <motion.header
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
      data-testid="site-navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-500 ${
        scrolled ? "bg-sand/85 backdrop-blur-md border-b border-charcoal/10" : "bg-transparent"
      }`}
    >
      <nav className="mx-auto max-w-7xl px-5 sm:px-8 h-32 flex items-center justify-between">
        <a href="#top" data-testid="nav-logo" className="flex items-center gap-3">
          <img
            src={BUSINESS.logo}
            alt="HomeCare by Nicole logo"
            className="h-24 w-auto sm:h-28 object-contain"
          />
        </a>

        <div className="hidden md:flex items-center gap-9">
          {LINKS.map((l) => (
            <a
              key={l.href}
              href={l.href}
              data-testid={`nav-link-${l.href.slice(1)}`}
              className="text-sm text-charcoal-soft hover:text-charcoal transition-colors relative after:absolute after:left-0 after:-bottom-1 after:h-px after:w-0 after:bg-sage after:transition-all hover:after:w-full"
            >
              {l.label}
            </a>
          ))}
          <a
            href="/portal/login"
            data-testid="nav-portal-link"
            className="text-sm text-sage-700 hover:text-charcoal transition-colors relative after:absolute after:left-0 after:-bottom-1 after:h-px after:w-0 after:bg-sage after:transition-all hover:after:w-full"
          >
            Portal
          </a>
        </div>

        <div className="flex items-center gap-3">
          <a
            href={BUSINESS.phoneHref}
            data-testid="nav-call-button"
            className="hidden sm:inline-flex items-center gap-2 rounded-full bg-sage text-white px-5 py-2.5 text-sm font-medium hover:bg-sage-dark transition-colors"
          >
            <Phone className="h-4 w-4" /> Call Nicole
          </a>
          <button
            data-testid="nav-menu-toggle"
            onClick={() => setOpen((o) => !o)}
            className="md:hidden text-charcoal p-2"
            aria-label="Toggle menu"
          >
            {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>
      </nav>

      {open && (
        <motion.div
          initial={{ height: 0, opacity: 0 }}
          animate={{ height: "auto", opacity: 1 }}
          className="md:hidden overflow-hidden bg-sand/95 backdrop-blur-md border-t border-charcoal/10"
        >
          <div className="px-6 py-5 flex flex-col gap-4">
            {LINKS.map((l) => (
              <a
                key={l.href}
                href={l.href}
                onClick={() => setOpen(false)}
                data-testid={`nav-mobile-link-${l.href.slice(1)}`}
                className="text-base text-charcoal-soft"
              >
                {l.label}
              </a>
            ))}
            <a
              href={BUSINESS.phoneHref}
              data-testid="nav-mobile-call"
              className="inline-flex items-center gap-2 rounded-full bg-sage text-white px-5 py-3 text-sm font-medium w-fit"
            >
              <Phone className="h-4 w-4" /> {BUSINESS.phoneDisplay}
            </a>
            <a
              href="/portal/login"
              data-testid="nav-mobile-portal"
              className="text-base text-sage-700"
            >
              Care Portal →
            </a>
          </div>
        </motion.div>
      )}
    </motion.header>
  );
};
